import { Fragment, useEffect } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { request, gql } from "graphql-request";
import { startOfDay } from "date-fns/startOfDay";
import safeJsonStringify from "safe-json-stringify";
import Stripe from "stripe";

import { loadStandardContent } from "utils/api";
import { PageHead } from "@/components/metadata";
import Layout from "@/components/layout";
import { ComponentMap } from "@/components/index";
import { useSession } from "@/components/providers/session";
import { klayAction } from "utils/klay";
import PermanentMakeup from "@/components/static/permanent-makeup";
import Embed from "@/components/finance/embed";
import { isBefore } from "date-fns";

const Contact = dynamic(() => import("@/components/forms/contact"));
const Login = dynamic(() => import("@/components/forms/login"));
const Register = dynamic(() => import("@/components/forms/register"));
const Newsletter = dynamic(() => import("@/components/newsletter"));
const Listing = dynamic(() => import("@/components/listing"));
const Callback = dynamic(() => import("@/components/forms/callback"));
const Model = dynamic(() => import("@/components/forms/models"));
const ForgotPassword = dynamic(
  () => import("@/components/forms/forgot-password")
);
const ResetPassword = dynamic(() => import("@/components/forms/reset-password"));
const PractitionerDays = dynamic(() => import("@/components/practitioner"));

const eventQuery = gql`
  query Event($id: String!, $start: timestamptz!) {
    event: event_detail(
      where: {
        _and: [
          { contentful_id: { _eq: $id } }
          { contentful_id: { _is_null: false } }
          { archived: { _eq: false } }
        ]
      }
    ) {
      id
      standardPrice: standard_price
      wasPrice: was_price
      package: is_package
      contentful_id
      programme_id
      options
      events(
        limit: 30
        order_by: { date: asc }
        where: {
          _and: [
            { start: { _gte: $start } }
            { status: { _eq: "NEW" } }
            { is_sellable: { _eq: true } }
          ]
        }
      ) {
        id
        end
        date
        type
        start
        detail_id
        seat_price
        settings: vtctSettings
        created_at
        seat_price
        discounted
        event_resources {
          id
          user_id
          type
          user {
            given_name
            user_assets(
              where: { assetByAsset: { type: { _eq: AVATAR } } }
              limit: 1
              order_by: { created_at: desc }
            ) {
              asset: assetByAsset {
                url
              }
            }
          }
        }
        countStudentRegistrations: registrations_aggregate(
          where: {
            _and: { status: { _neq: "CANCELLED" }, type: { _eq: "STUDENT" } }
          }
        ) {
          aggregate {
            count
          }
        }
        registrations(where: { type: { _eq: "STUDENT" } }) {
          id
        }
        detail {
          id
          title
          programme_id
          elearning_course_id
          is_package
          standard_price
        }
        seats
        status
        price: seat_price
      }
    }
  }
`;

const eventsQuery = gql`
  query Event($ids: [String!], $start: timestamptz!) {
    events: event_detail(
      where: {
        _and: [
          { contentful_id: { _in: $ids } }
          { contentful_id: { _is_null: false } }
        ]
      }
    ) {
      id
      programme_id
      standardPrice: standard_price
      wasPrice: was_price
      package: is_package
      contentful_id
      options
      events(
        limit: 30
        order_by: { date: asc }
        where: {
          _and: [
            { start: { _gte: $start } }
            { status: { _eq: "NEW" } }
            { is_sellable: { _eq: true } }
          ]
        }
      ) {
        id
        end
        date
        type
        start
        detail_id
        seat_price
        created_at
        seat_price
        discounted
        event_resources {
          id
          user_id
          type
          user {
            given_name
          }
        }
        countStudentRegistrations: registrations_aggregate(
          where: {
            _and: { status: { _neq: "CANCELLED" }, type: { _eq: "STUDENT" } }
          }
        ) {
          aggregate {
            count
          }
        }
        registrations(where: { type: { _eq: "STUDENT" } }) {
          id
        }
        detail {
          id
          title
          elearning_course_id
          is_package
        }
        seats
        status
        price: seat_price
      }
    }
  }
`;

export default function Page(props) {
  const session = useSession();
  const router = useRouter();

  const entry = props.entry || {};

  const { category, type, url, content, metadata } = entry.fields || {};

  useEffect(() => {
    if (window?.location?.pathname?.match(/courses/g)?.length > 0) {
      try {
        const l = window.location.pathname.split("/");

        const payload: { [key: string]: any } = {
          URL: window.location.href,
          Category: category,
          $path_name: window.location.pathname,
          ...(type === "Course" ? klayAction(props.entry) : {}),
        };

        if (session.user && session.user.attributes) {
          payload.$email = session.user.attributes.email;
        }

        if (window.location.pathname.match(/\/.+?/g).length === 3) {
          payload.Category = (l[l.length - 2] as string)
            .replace(/-/g, " ")
            .replace(/(^\w{1})|(\s{1}\w{1})/g, (match) => match.toUpperCase());

          // @ts-ignore
          klaviyo && klaviyo.push(["trackViewedItem", payload]);
        }

        if (window.location.pathname.match(/\/.+?/g).length === 2) {
          payload.Category = (l[l.length - 1] as string)
            .replace(/-/g, " ")
            .replace(/(^\w{1})|(\s{1}\w{1})/g, (match) => match.toUpperCase());
        }

        const action =
          window.location.pathname.match(/\/.+?/g).length === 2
            ? "Viewed Category"
            : "Viewed Course";

        // @ts-ignore
        klaviyo && klaviyo.push(["track", action, payload]);
      } catch (err) {
        console.log(err);
      }

      try {
        if (props.events && props.events > 0) {
          // @ts-ignore
          fbq &&
            // @ts-ignore
            fbq("track", "ViewContent", {
              content_ids: props.events.map((x) => x.id),
              content_category: "Training Courses",
              content_name: props.entry.fields.title,
              content_type: "product",
              value: props.event.standardPrice / 100,
            });
        }

        if (props.event) {
          // @ts-ignore
          fbq &&
            // @ts-ignore
            fbq("track", "ViewContent", {
              content_ids: [props.event.id],
              content_category: "Training Courses",
              content_name: props.entry.fields.title,
              content_type: "product",
              value: props.event.standardPrice / 100,
            });
        }
      } catch (err) {
        console.log("99 err", err);
      }
    }

    try {
      // @ts-ignore
      if (window.clarity && session && session.user?.attributes?.email) {
        // @ts-ignore
        window.clarity("identify", session.user?.attributes?.email);
      }
    } catch (err) {
      console.log(err);
    }
  }, [router.pathname, props.entry]);

  return (
    <>
      <PageHead
        metadata={metadata}
        {...props}
        event={props.event}
        primary={props.entry}
      />
      <Layout {...props} siteData={props.siteData}>
        <>
          {router.asPath === "/start-a-career-in-permanent-makeup/" ? (
            <PermanentMakeup {...props} />
          ) : router.asPath === "/practitioner-days/" ? (
            <PractitionerDays {...props} />
          ) : (
            <>
              {Array.isArray(content) &&
                content.map(({ fields, sys }, index) => {
                  if (url === "/models" && index === content.length - 1) {
                    const X = ComponentMap[sys.contentType.sys.id];
                    return (
                      <Fragment key={`${index}-${sys.contentType.sys.id}`}>
                        <Model />
                        {X ? (
                          <X
                            fields={fields}
                            sys={sys}
                            first={index === 0}
                            includes={props.includes}
                            primary={props.entry}
                          />
                        ) : null}
                      </Fragment>
                    );
                  } else if (ComponentMap[sys.contentType.sys.id]) {
                    const Cmp = ComponentMap[sys.contentType.sys.id];
                    return (
                      <Cmp
                        {...props}
                        key={`${index}-${sys.contentType.sys.id}`}
                        fields={fields}
                        sys={sys}
                        first={index === 0}
                        event={props.event}
                        includes={props.includes}
                        primary={props.entry}
                      />
                    );
                  }
                  console.log(`NOT FOUND: ${sys.contentType.sys.id}`);
                  return null;
                })}
              {url === "/contact" && <Contact />}
              {url === "/sign-up" && <Register />}
              {url === "/sign-in" && <Login />}
              {url === "/forgot-password" && <ForgotPassword />}
              {url === "/reset-password" && <ResetPassword />}
              {type && type === "Course" && (
                <Callback entry={props.entry} event={props.event} />
              )}
              {url === "/finance" && (
                <div id="ee" className="relative w-full">
                  <div
                    id="snapfinance-page"
                    style={{
                      margin: "0 !important",
                      width: "100%",
                      maxWidth: "100%",
                    }}
                  />
                  <Embed />
                </div>
              )}

              {props.isCourseListing && <Listing {...props} />}
              {props.isBlog && <Listing {...props} />}
            </>
          )}
        </>
        <Newsletter />
      </Layout>
    </>
  );
}

function createClient() {
  const contentful = require("contentful");
  const host = process.env.NEXT_CONTENTFUL_BASE || "https://cdn.contentful.com";
  const key =
    process.env.NEXT_CONTENTFUL_PREVIEW_KEY || process.env.NEXT_CONTENTFUL_KEY;
  const client = contentful.createClient({
    host,
    space: process.env.NEXT_CONTENTFUL_SPACE || "",
    accessToken: key,
  });
  return client;
}

const virtualQuery = gql`
  query VirtualCourses {
    event_detail(
      where: {
        _or: [
          { events: { type: { _eq: "virtual" } } }
          { events: { type: { _eq: "blended" } } }
        ]
      }
    ) {
      contentful_id
    }
  }
`;

export async function getStaticProps({ params, preview = false }) {
  const client = createClient();
  const isCourseListing =
    (params.page.length === 2 && params.page.includes("courses")) ||
    (params.page.length === 1 && params.page[0] === "courses");
  const last = params.page[params.page.length - 1];
  let virtualIds = null;

  if (last.includes("virtual")) {
    const virtualData: any = await request(
      process.env.NEXT_PUBLIC_API_URL,
      virtualQuery,
      {},
      {
        "x-hasura-admin-secret": process.env.NEXT_API_SECRET,
      }
    );
    virtualIds = virtualData.event_detail.map((x) => x.contentful_id);
  }

  const elearning =
    isCourseListing && params.page.length === 2 && last === "elearning";

  const courseParams =
    virtualIds && virtualIds.length > 0
      ? {
          content_type: "page",
          "sys.id[in]": virtualIds.join(","),
          include: 3,
        }
      : elearning
      ? {
          content_type: "page",
          "fields.categories": "online",
          include: 3,
        }
      : isCourseListing && params.page.length === 2
      ? {
          content_type: "page",
          "fields.categories": last,
          include: 3,
        }
      : {
          content_type: "page",
          "fields.type": "Course",
          include: 3,
        };

  const [entries, siteData, courses] = await Promise.all([
    client.getEntries({
      content_type: "page",
      "fields.url": `/${params.page.join("/")}`,
      include: 3,
    }),
    loadStandardContent(),
    isCourseListing ? client.getEntries(courseParams) : {},
  ]);

  const isBlog = params.page.length === 1 && params.page[0] === "blog";

  let blogContent = null;

  if (isBlog) {
    blogContent = await client.getEntries({
      content_type: "page",
      "fields.type": "Article",
      include: 3,
    });
  }

  const stringifiedData = safeJsonStringify(blogContent || courses);
  const data = JSON.parse(stringifiedData);

  let eventResults: any = null;
  let eventResult: any = null;
  const stripe = new Stripe(process.env.NEXT_ONLINE_STRIPE_PK, {
    apiVersion: "2024-06-20",
  });

  if (data && data.items) {
    // if (!elearning) {
    const ids = data.items.map((x) => x.sys.id);
    eventResults = await request(
      process.env.NEXT_PUBLIC_API_URL,
      eventsQuery,
      {
        ids: ids,
        start: startOfDay(new Date()),
      },
      {
        "x-hasura-admin-secret": process.env.NEXT_API_SECRET,
      }
    );
    // }

    // if (elearning) {
    data.items = await Promise.all(
      data.items.map(async (x) => {
        let pricing = null;

        if (x?.fields?.data?.priceId) {
          pricing = await stripe.prices.retrieve(x?.fields?.data?.priceId);
        }

        if (x.fields.data && x.fields.data.paymentLinkId) {
          const link = await stripe.paymentLinks.retrieve(
            x.fields.data.paymentLinkId,
            {
              expand: ["line_items"],
            }
          );

          console.log(link, "LINK");

          return {
            ...x,
            fields: {
              ...x.fields,
              link,
              pricing,
            },
          };
        }
        return x;
      })
    );
    // }
  }

  if (
    entries &&
    entries.items &&
    entries.items.length === 1 &&
    entries.items[0].fields.type === "Course"
  ) {
    eventResult = await request(
      process.env.NEXT_PUBLIC_API_URL,
      eventQuery,
      {
        id: entries.items[0].sys.id,
        start: startOfDay(new Date()),
      },
      {
        "x-hasura-admin-secret": process.env.NEXT_API_SECRET,
      }
    );
  }

  if (
    entries &&
    entries.items &&
    entries.items.length === 1 &&
    entries.items[0].fields.type === "Online Course" &&
    entries.items[0].fields?.data?.priceId
  ) {
    const pricing = await stripe.prices.retrieve(
      entries.items[0].fields.data.priceId
    );
    entries.items[0].fields.pricing = pricing;
  }

  if (
    entries &&
    entries.items &&
    entries.items.length === 1 &&
    entries.items[0].fields.type === "Online Course" &&
    entries.items[0].fields.data.paymentLinkId
  ) {
    const link = await stripe.paymentLinks.retrieve(
      entries.items[0].fields.data.paymentLinkId,
      {
        expand: ["line_items"],
      }
    );
    entries.items[0].fields.link = link;
  }

  function filterEvents(x) {
    if (x.registrations.length >= x.seats) {
      return false;
    }

    if (isBefore(new Date(x.start), new Date())) {
      return false;
    }

    return true;
  }

  function filterEventEvents(x) {
    return x.events.filter(filterEvents);
  }

  const events =
    eventResults && eventResults.events
      ? eventResults && eventResults.events.filter(filterEventEvents)
      : [];

  const event =
    eventResult && eventResult.event && eventResult.event[0]
      ? eventResult.event[0]
      : null;

  if (event && event.events) {
    event.events = event.events.filter(filterEvents);
  }

  const props = {
    entry: entries && entries.items ? entries.items[0] : {},
    includes: (entries && entries.includes) || {},
    siteData,
    courses: data || null,
    isCourseListing,
    isBlog,
    events,
    event,
  };

  return {
    props,
    revalidate: 60 * 60,
  };
}

export async function getStaticPaths() {
  const client = createClient();

  const pages = await client.getEntries({
    content_type: "page",
    limit: 500,
  });

  const paths = pages.items
    .map((entry) => {
      const {
        fields: { url },
      } = entry;

      if (url === "/") {
        return null;
      }
      if (url === "/training-course-calendar") {
        return null;
      }

      if (!url) {
        console.log("entry", entry);
        return null;
      }

      const pageParams = url === "/" ? [url] : url.split("/").filter(Boolean);

      return {
        params: {
          page: pageParams.map((x) => x.trim()),
        },
      };
    })
    .filter(Boolean);

  return {
    paths: paths.filter(Boolean),
    fallback: false,
  };
}
